import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Send, Users, Gem } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const paletteFor = (idx) => {
  const p = [
    { grad: "from-amber-400 to-rose-500", ring: "ring-amber-400/50" },
    { grad: "from-sky-400 to-indigo-500", ring: "ring-sky-400/50" },
    { grad: "from-emerald-400 to-teal-500", ring: "ring-emerald-400/50" },
    { grad: "from-rose-500 to-purple-500", ring: "ring-rose-500/50" },
    { grad: "from-orange-400 to-yellow-500", ring: "ring-orange-400/50" },
  ];
  return p[idx % p.length];
};

export default function SceneDetail() {
  const { id } = useParams();
  const { refresh } = useAuth();
  const [scene, setScene] = useState(null);
  const [text, setText] = useState("");
  const [speaker, setSpeaker] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const load = () => api.get(`/scenes/${id}`).then(r => setScene(r.data)).catch(() => {});
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [scene?.messages?.length, busy]);

  if (!scene) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">Opening the room…</div>;

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    const msg = text.trim();
    setText("");
    setBusy(true);
    setScene(s => ({ ...s, messages: [...(s.messages || []), { role: "user", text: msg, created_at: new Date().toISOString() }] }));
    try {
      const { data } = await api.post(`/scenes/${id}/message`, { message: msg, speaker: speaker || undefined });
      setScene(s => ({ ...s, messages: [...s.messages, { role: "character", character: data.speaker, text: data.line, created_at: data.created_at }] }));
      await refresh();
    } catch (e) {
      const m = e.response?.data?.detail || "Failed";
      toast.error(m);
      setScene(s => ({ ...s, messages: s.messages.slice(0, -1) }));
    } finally { setBusy(false); }
  };

  const characters = scene.characters || [];
  const characterIdx = (name) => characters.findIndex(c => c.name === name);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
      <Link to="/scenes" className="inline-flex items-center gap-2 text-xs text-slate-500 hover:text-amber-300 mb-6">
        <ArrowLeft className="h-3.5 w-3.5" /> All scenes
      </Link>

      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-rose-400 mb-2 flex items-center gap-2">
          <Users className="h-3 w-3" /> Group Scene
        </div>
        <h1 data-testid="scene-title" className="font-serif text-3xl md:text-4xl font-bold tracking-tight">{scene.title}</h1>
        <p className="mt-2 text-slate-400 max-w-2xl">{scene.prompt}</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {characters.map((c, i) => {
          const pal = paletteFor(i);
          const active = speaker === c.name;
          return (
            <button
              key={c.name}
              type="button"
              data-testid={`cast-${c.name.replace(/\s+/g, '-')}`}
              onClick={() => setSpeaker(active ? "" : c.name)}
              className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition ${active ? "border-amber-500/60 bg-amber-500/10 text-amber-100" : "border-white/10 bg-[#0F1117] text-slate-300 hover:border-white/20"}`}
              title={c.bio}
            >
              <span className={`h-5 w-5 rounded-full bg-gradient-to-br ${pal.grad} text-black text-[10px] font-bold flex items-center justify-center`}>
                {c.name.slice(0,1)}
              </span>
              <span className="font-serif">{c.name}</span>
              <span className="text-[9px] text-slate-500 uppercase tracking-widest">{c.story_genre}</span>
            </button>
          );
        })}
      </div>

      <div ref={scrollRef} data-testid="scene-messages" className="rounded-3xl border border-white/10 bg-[#0F1117] p-4 md:p-6 h-[52vh] overflow-y-auto space-y-4">
        {(scene.messages || []).length === 0 && (
          <div className="text-center text-slate-500 text-sm py-10">
            The room is silent. Say something, or pick a character below and prompt them to speak.
          </div>
        )}
        {(scene.messages || []).map((m, i) => {
          if (m.role === "user") {
            return (
              <div key={i} className="flex justify-end">
                <div className="max-w-[75%] rounded-2xl rounded-br-md px-4 py-2.5 text-sm bg-amber-500/15 border border-amber-500/30 text-amber-50">{m.text}</div>
              </div>
            );
          }
          const idx = Math.max(0, characterIdx(m.character));
          const pal = paletteFor(idx);
          return (
            <div key={i} className="flex justify-start gap-3">
              <div className={`h-8 w-8 rounded-full bg-gradient-to-br ${pal.grad} text-black text-xs font-bold flex items-center justify-center ring-2 ${pal.ring}`}>
                {(m.character || "?").slice(0,1)}
              </div>
              <div className="max-w-[75%]">
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{m.character}</div>
                <div className="rounded-2xl rounded-bl-md px-4 py-2.5 text-sm text-slate-100 font-serif italic bg-white/5 border border-white/10">{m.text}</div>
              </div>
            </div>
          );
        })}
        {busy && (
          <div className="flex justify-start">
            <div className="bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-sm text-slate-400 italic">
              <span className="inline-flex gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" style={{animationDelay: "150ms"}} />
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" style={{animationDelay: "300ms"}} />
              </span>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="mt-4 flex items-center gap-2">
        <Input
          data-testid="scene-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={speaker ? `Ask ${speaker} something…` : "Speak to the room… (or pick a character above)"}
          disabled={busy}
          className="bg-[#0F1117] border-white/10 h-11"
        />
        <Button data-testid="scene-send" type="submit" disabled={busy || !text.trim()} className="rounded-full bg-amber-500 hover:bg-amber-400 text-black h-11 w-11 p-0">
          <Send className="h-4 w-4" />
        </Button>
      </form>
      <div className="mt-2 text-[11px] text-slate-500 font-mono uppercase tracking-widest text-right inline-flex items-center gap-1 float-right">
        <Gem className="h-3 w-3 text-amber-400" /> Unlimited free turns · no per-message fees
      </div>
    </div>
  );
}
